import { type ContractSuretyType, SuretyType } from "@inrev/common";
import { useFetchSfaaCodes } from "@portal/api/sfaa-code/admin";
import { FormComponent, FormFieldGroup, FormRow, FormSection } from "@portal/components/form";
import {
	FormDateComponent,
	FormDropdownComponent,
	FormInputComponent,
} from "@portal/components/form/fieldLayouts";
import {
	FormAgencySelector,
	FormAgentSelector,
	FormSfaaCodeSelect,
} from "@portal/components/form/fields";
import { useFormContext, useWatch } from "react-hook-form";
import {
	BID_CLOSED_REASON_OPTIONS,
	BOND_STATUS_OPTIONS,
	FINAL_CLOSED_REASON_OPTIONS,
} from "../constants";
import type { AdminImportBondViewFormData, DropdownOption } from "../types";

type BondDetailsSectionProps = {
	contractSuretyType: ContractSuretyType | "";
	isBid: boolean;
	isClosed: boolean;
	accountOptions: DropdownOption[];
	accountReadonly: boolean;
	principalOptions: DropdownOption[];
	carrierOptions: DropdownOption[];
	bidBondOptions: DropdownOption[];
	bidBondPlaceholder: string;
};

export const BondDetailsSection = ({
	contractSuretyType,
	isBid,
	isClosed,
	accountOptions,
	accountReadonly,
	principalOptions,
	carrierOptions,
	bidBondOptions,
	bidBondPlaceholder,
}: BondDetailsSectionProps) => {
	const formMethods = useFormContext<AdminImportBondViewFormData>();
	const agencyId = useWatch({ control: formMethods.control, name: "agencyId" });
	const suretyAccountId = useWatch({ control: formMethods.control, name: "bond.suretyAccountId" });
	const { sfaaCodes, sfaaCodesIsLoading } = useFetchSfaaCodes();

	return (
		<FormSection header="Bond Details">
			<FormFieldGroup condensed>
				<FormRow>
					<FormComponent formMethods={formMethods} name="agencyId" label="Agency" inline>
						<FormAgencySelector
							control={formMethods.control}
							name="agencyId"
							optionsClassName="max-h-[35vh]"
						/>
					</FormComponent>
					<FormComponent formMethods={formMethods} name="agentId" label="Agent" inline>
						<FormAgentSelector
							control={formMethods.control}
							name="agentId"
							agencyId={agencyId}
							optionsClassName="max-h-[35vh]"
						/>
					</FormComponent>
				</FormRow>
				<FormRow>
					<FormDropdownComponent
						formMethods={formMethods}
						name="bond.suretyAccountId"
						label="Account"
						inline
						options={accountOptions}
						placeholder="Select one"
						readonly={accountReadonly}
						optionsClassName="max-h-[35vh]"
					/>
					<FormDropdownComponent
						formMethods={formMethods}
						name="bond.companyContactId"
						label="Principal"
						inline
						options={principalOptions}
						placeholder={suretyAccountId ? "Select one" : "Select an account first"}
						readonly={accountReadonly || !suretyAccountId}
						optionsClassName="max-h-[35vh]"
					/>
				</FormRow>
				{contractSuretyType === "bid_to_final" && (
					<FormDropdownComponent
						formMethods={formMethods}
						name="bond.bondCreatedFromId"
						label="Bid Bond"
						inline
						options={bidBondOptions}
						placeholder={bidBondPlaceholder}
					/>
				)}
				<FormRow>
					<FormDropdownComponent
						formMethods={formMethods}
						name="bond.carrierName"
						label="Carrier"
						inline
						options={carrierOptions}
						placeholder="Select one"
					/>
					<FormInputComponent
						formMethods={formMethods}
						name="bond.number"
						label="Bond Number"
						inline
					/>
					<FormDropdownComponent
						formMethods={formMethods}
						name="bond.status"
						label="Status"
						inline
						options={BOND_STATUS_OPTIONS}
						placeholder="Select one"
					/>
				</FormRow>
				<FormRow>
					<FormComponent formMethods={formMethods} name="bond.sfaaCodeId" label="SFAA Code" inline>
						<FormSfaaCodeSelect
							control={formMethods.control}
							name="bond.sfaaCodeId"
							sfaaCodes={sfaaCodes}
							isLoading={sfaaCodesIsLoading}
							suretyType={SuretyType.contract}
						/>
					</FormComponent>
				</FormRow>
				<FormRow include={isClosed}>
					<FormDropdownComponent
						formMethods={formMethods}
						name="bond.data.closedReason"
						label="Closed Reason"
						inline
						options={isBid ? BID_CLOSED_REASON_OPTIONS : FINAL_CLOSED_REASON_OPTIONS}
						placeholder="Select one"
					/>
					<FormDateComponent
						formMethods={formMethods}
						name="bond.closedAt"
						label="Closed Date"
						inline
					/>
				</FormRow>
			</FormFieldGroup>
		</FormSection>
	);
};
